/**
 * Participant roster (`/me/events/:id/participants`).
 *
 * The organizer-only view: every constraint the engine will use. A member is sent to
 * the share page by the layout, so this page renders the organizer's copy; the
 * fields only the creator may see stay optional here because the same payload is
 * served to members, who receive the shared projection without the note, the budget
 * and the account id.
 */

import { useTranslation } from 'react-i18next'

import type { ParticipantDto, PublicParticipantDto } from '@shared/types'

import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { EmptyState, Stat, StatGrid } from '../../../components/ui/primitives'
import { activityLabelKey, formatDuration, formatLocalShort, modeLabelKey } from '../../../lib/format'
import { currentLocale } from '../../../lib/i18n'
import { useEventDetailContext } from './layout'

/**
 * A roster row as the API sends it.
 *
 * The creator receives `ParticipantDto`; a member receives `PublicParticipantDto`,
 * its strict subset. Everything the shared view carries is required, and the
 * creator-only fields stay optional so the page never invents a budget or a note for
 * a reader who was not sent one.
 */
type RosterRow = PublicParticipantDto &
  Partial<Pick<ParticipantDto, 'eventId' | 'userId' | 'travelGroupId' | 'budget' | 'note'>>

export function EventParticipantsPage() {
  const { t } = useTranslation()
  const { event } = useEventDetailContext()
  const locale = currentLocale()
  const participants: RosterRow[] = event.participants

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-base font-semibold">
        {t('events.peopleAndUnits', {
          people: t('events.participantCount', {
            count: participants.reduce((total, row) => total + row.headcount, 0),
          }),
          units: t('events.unitCount', { count: participants.length }),
        })}
      </h2>

      {participants.length === 0 ? (
        <EmptyState title={t('common.empty')} description={t('events.participantCount', { count: 0 })} />
      ) : (
        <div className="grid gap-3">
          {participants.map((participant) => {
            const timeWindow =
              participant.availableTimeStart && participant.availableTimeEnd
                ? `${formatLocalShort(participant.availableTimeStart, locale)} → ${formatLocalShort(
                    participant.availableTimeEnd,
                    locale,
                  )}`
                : (participant.availableTimeStart ??
                  participant.availableTimeEnd ??
                  t('common.unknown'))

            return (
              <Card key={participant.id}>
                <CardHeader>
                  <CardTitle>{participant.nickname}</CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Two label/value pairs per row on a phone: nine stacked pairs
                      made one participant several screens tall. */}
                  <StatGrid className="grid-cols-2">
                    <Stat
                      label={t('join.locationLabel')}
                      value={participant.departureLabel ?? t('common.unknown')}
                    />
                    <Stat
                      label={t('join.stepModes')}
                      value={participant.modes.map((mode) => t(modeLabelKey(mode))).join(' / ')}
                    />
                    <Stat label={t('join.stepTime')} value={timeWindow} />
                    <Stat
                      label={t('join.maxTravelLabel')}
                      value={formatDuration(participant.maxTravelMinutes, locale)}
                    />
                    {participant.budget === undefined ? null : (
                      <Stat label={t('budget.label')} value={t(`budget.${participant.budget}`)} />
                    )}
                    <Stat
                      label={t('join.stepActivities')}
                      value={
                        participant.attendingActivities.length === 0
                          ? t('common.unknown')
                          : participant.attendingActivities
                              .map((activity) => t(activityLabelKey(activity)))
                              .join(' / ')
                      }
                    />
                    <Stat label={t('join.noteLabel')} value={participant.note ?? t('common.empty')} />
                  </StatGrid>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}
    </section>
  )
}
